import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Quote,
  Underline as UnderlineIcon,
} from "lucide-react";

interface RichTextEditorProps {
  /** Sanitized HTML value ("" for empty). */
  value: string;
  onChange: (html: string) => void;
}

const toolbarButton = (active: boolean) =>
  active
    ? "rounded-lg bg-primary-95 p-1.5 text-primary-40 dark:bg-primary-20 dark:text-primary-90"
    : "rounded-lg p-1.5 text-grey-40 hover:bg-grey-95 dark:text-grey-80 dark:hover:bg-primary-20";

/**
 * Small WYSIWYG for the landing page's main text areas: bold/italic/underline,
 * headings, lists and quotes — exactly the subset the backend sanitizer keeps.
 * Output is HTML; the server re-sanitizes on save.
 */
const RichTextEditor = ({ value, onChange }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: false,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          "min-h-32 px-3 py-2 focus:outline-none text-grey-20 dark:text-white " +
          "[&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 " +
          "[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-lg [&_h3]:font-semibold " +
          "[&_blockquote]:border-l-2 [&_blockquote]:border-grey-80 [&_blockquote]:pl-3 [&_blockquote]:italic dark:[&_blockquote]:border-grey-50",
      },
    },
    onUpdate: ({ editor: current }) => {
      onChange(current.isEmpty ? "" : current.getHTML());
    },
  });

  // Adopt external resets (e.g. settings refetched) without echo loops.
  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.isEmpty ? "" : editor.getHTML();
    if (value !== currentHtml) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const actions = [
    { icon: Bold, active: editor.isActive("bold"), run: () => editor.chain().focus().toggleBold().run() },
    { icon: Italic, active: editor.isActive("italic"), run: () => editor.chain().focus().toggleItalic().run() },
    { icon: UnderlineIcon, active: editor.isActive("underline"), run: () => editor.chain().focus().toggleUnderline().run() },
    { icon: Heading3, active: editor.isActive("heading", { level: 3 }), run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { icon: List, active: editor.isActive("bulletList"), run: () => editor.chain().focus().toggleBulletList().run() },
    { icon: ListOrdered, active: editor.isActive("orderedList"), run: () => editor.chain().focus().toggleOrderedList().run() },
    { icon: Quote, active: editor.isActive("blockquote"), run: () => editor.chain().focus().toggleBlockquote().run() },
  ];

  return (
    <div className="mt-1 rounded-xl border border-grey-80 bg-white dark:border-grey-50 dark:bg-primary-10">
      <div className="flex flex-wrap gap-1 border-b border-grey-90 p-1.5 dark:border-grey-50">
        {actions.map(({ icon: Icon, active, run }, index) => (
          <button key={index} type="button" className={toolbarButton(active)} onMouseDown={(e) => e.preventDefault()} onClick={run}>
            <Icon size={16} />
          </button>
        ))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
